import { useEffect, useState } from "react";
import {
  fetchLoveFruitMeta,
  fetchLoveFruitQuestions,
  submitLoveFruitAnswers,
} from "../api/loveFruitApi";
import { GuestMatchHint } from "../components/WeChatLoginButton";

const STORAGE_KEY = "love_fruit_result_v1";

export default function LoveFruitSection() {
  const [meta, setMeta] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [phase, setPhase] = useState("intro");
  const [result, setResult] = useState(null);
  const [fullOpen, setFullOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [m, q] = await Promise.all([
          fetchLoveFruitMeta(),
          fetchLoveFruitQuestions(),
        ]);
        if (cancelled) return;
        setMeta(m);
        setQuestions(q?.items || []);
        try {
          const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null");
          if (saved?.fruit) setResult(saved);
        } catch {
          /* ignore */
        }
      } catch (e) {
        if (!cancelled) setError(e.message || "加载失败");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const current = questions[index];
  const progress = questions.length
    ? Math.round((Object.keys(answers).length / questions.length) * 100)
    : 0;
  const fruit = result?.fruit;

  const startQuiz = () => {
    setAnswers({});
    setIndex(0);
    setResult(null);
    setFullOpen(false);
    setError("");
    setPhase("quiz");
    localStorage.removeItem(STORAGE_KEY);
  };

  const pick = async (key) => {
    if (!current || busy) return;
    const next = { ...answers, [String(current.id)]: key };
    setAnswers(next);
    if (index < questions.length - 1) {
      setIndex((i) => i + 1);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const data = await submitLoveFruitAnswers(next);
      setResult(data);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      setPhase("result");
    } catch (e) {
      setError(e.message || "提交失败");
    } finally {
      setBusy(false);
    }
  };

  const shareResult = async () => {
    const text = `我在「婚恋有道」测出爱情水果是【${fruit?.name}】——${fruit?.tagline}。你也来测测？`;
    try {
      if (navigator.share) {
        await navigator.share({ title: "爱情水果测评", text, url: window.location.href });
      } else {
        await navigator.clipboard.writeText(`${text}\n${window.location.href}`);
        alert("已复制分享文案");
      }
    } catch {
      /* user cancel */
    }
  };

  if (loading) {
    return <p className="text-center text-sm text-gray-400 py-16">加载中…</p>;
  }

  return (
    <div className="w-full max-w-lg mx-auto space-y-5">
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          {error}
        </div>
      )}

      {phase === "intro" && (
        <section className="space-y-5 animate-[fadeIn_0.35s_ease]">
          <div className="rounded-2xl overflow-hidden shadow-lg bg-gradient-to-br from-teal-200 via-cyan-100 to-emerald-100 p-5 relative min-h-[200px]">
            <p className="text-xl font-extrabold text-teal-900 leading-snug max-w-[70%]">
              {meta?.subtitle || "测测你的爱情性格水果是什么？"}
            </p>
            <p className="text-sm text-teal-800/80 mt-2 max-w-[75%]">
              {meta?.tagline}
            </p>
            <button
              type="button"
              onClick={startQuiz}
              className="mt-4 inline-flex px-3 py-1.5 rounded-full bg-teal-800 text-white text-xs font-medium"
            >
              点击测试 ▶
            </button>
            <span className="absolute right-4 bottom-4 text-5xl">🍒🍊</span>
          </div>

          <div className="space-y-1 px-1">
            <h2 className="text-lg font-bold text-gray-800">
              {meta?.title || "爱情水果"}
            </h2>
            <p className="text-sm text-gray-500">{meta?.tagline}</p>
            <p className="text-xs text-gray-400 pt-1">
              共 {meta?.question_count || 16} 题 · 游客可直接测完 · {meta?.disclaimer}
            </p>
          </div>

          <GuestMatchHint redirect="/love-map?tab=fruit" />

          <button
            type="button"
            onClick={startQuiz}
            className="w-full py-3.5 rounded-xl bg-gradient-to-r from-orange-400 to-amber-500 text-white font-bold shadow-md"
          >
            开始答题
          </button>

          {result?.fruit && (
            <button
              type="button"
              onClick={() => setPhase("result")}
              className="w-full py-3 rounded-xl border border-rose-200 text-rose-600 text-sm font-medium"
            >
              查看已有报告
            </button>
          )}
        </section>
      )}

      {phase === "quiz" && current && (
        <section className="space-y-4 animate-[fadeIn_0.25s_ease]">
          <div className="space-y-2">
            <div className="flex justify-between text-xs text-gray-500">
              <span>
                第 {index + 1} / {questions.length} 题
              </span>
              <span>{progress}%</span>
            </div>
            <div className="h-2 bg-white rounded-full border border-rose-100 overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-orange-400 to-rose-400 transition-all"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>

          <div className="rounded-3xl bg-white shadow-xl border border-rose-50 p-5 space-y-4">
            <h3 className="text-lg font-bold text-gray-800 leading-snug">
              {current.question}
            </h3>
            <div className="space-y-2.5">
              {(current.options || []).map((opt) => (
                <button
                  key={opt.key}
                  type="button"
                  disabled={busy}
                  onClick={() => pick(opt.key)}
                  className="w-full text-left px-4 py-3.5 rounded-2xl border border-gray-200 hover:border-rose-300 bg-white transition active:scale-[0.98]"
                >
                  <span className="font-semibold mr-2 uppercase text-rose-500">
                    {opt.key}.
                  </span>
                  {opt.text}
                </button>
              ))}
            </div>
          </div>

          <div className="flex justify-between">
            <button
              type="button"
              disabled={index === 0 || busy}
              onClick={() => setIndex((i) => Math.max(0, i - 1))}
              className="text-sm text-gray-500 disabled:opacity-40"
            >
              ← 上一题
            </button>
            <button
              type="button"
              onClick={() => setPhase("intro")}
              className="text-sm text-gray-400"
            >
              退出
            </button>
          </div>
          {busy && <p className="text-center text-sm text-gray-400">生成报告中…</p>}
        </section>
      )}

      {phase === "result" && fruit && (
        <section
          className="space-y-5 animate-[fadeIn_0.35s_ease] rounded-2xl p-1"
          style={{
            backgroundImage:
              "linear-gradient(#fdf2f8,#fff7ed), repeating-linear-gradient(0deg,transparent,transparent 19px,rgba(251,207,232,.35) 20px), repeating-linear-gradient(90deg,transparent,transparent 19px,rgba(251,207,232,.35) 20px)",
          }}
        >
          <div className="px-2 pt-3 text-center">
            <p className="text-xs text-violet-500 font-medium">婚恋有道</p>
            <h2 className="text-2xl font-black text-rose-600 tracking-wide mt-1">
              爱情性格研究报告
            </h2>
          </div>

          <div className="bg-white rounded-2xl shadow-md border border-rose-100 p-5 mx-1">
            <div className="flex gap-4 items-center">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-500">我是：</p>
                <p className="text-sm text-gray-700 mt-1 leading-relaxed">
                  一颗{fruit.tagline}的
                </p>
                <p
                  className="text-3xl font-black mt-1"
                  style={{ color: fruit.color }}
                >
                  {fruit.name}
                </p>
              </div>
              <div
                className={`w-24 h-24 rounded-2xl bg-gradient-to-br ${fruit.bg || "from-rose-50 to-white"} flex items-center justify-center text-5xl shrink-0`}
              >
                {fruit.emoji}
              </div>
            </div>
            <div className="border-t border-dashed border-rose-100 mt-4 pt-4">
              <p className="text-xs text-gray-500 mb-2">{fruit.name}的关键词：</p>
              <div className="flex flex-wrap gap-2">
                {(fruit.keywords || []).map((k) => (
                  <span
                    key={k}
                    className="px-3 py-1 rounded-full text-xs font-medium bg-rose-50 text-rose-600"
                  >
                    {k}
                  </span>
                ))}
              </div>
            </div>
            <p className="text-sm text-gray-600 mt-4 leading-relaxed">{fruit.summary}</p>
          </div>

          <div className="mx-1 space-y-2">
            <h3 className="font-bold text-gray-800 border-b-4 border-rose-300 inline-block pb-0.5">
              我向往的恋爱关系
            </h3>
            <ul className="space-y-2 text-sm text-gray-700">
              {(fruit.desires || []).map((d) => (
                <li key={d} className="flex gap-2">
                  <span className="text-rose-400 mt-1">●</span>
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>

          {(result.suitables || []).length > 0 && (
            <div className="mx-1 space-y-3">
              <h3 className="font-bold text-gray-800">我最适合的恋爱类型是</h3>
              <div className="grid grid-cols-2 gap-3">
                {result.suitables.map((s) => (
                  <div
                    key={s.key}
                    className="bg-white rounded-xl border border-rose-100 p-3 text-center shadow-sm"
                  >
                    <div className="text-4xl mb-2">{s.emoji}</div>
                    <p className="text-xs font-semibold text-gray-800 leading-snug">
                      {s.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {result.demo_pair && (
            <div className="mx-1 rounded-2xl bg-gradient-to-r from-sky-50 to-rose-50 border border-white p-4 space-y-3">
              <div className="text-center">
                <span className="inline-block px-3 py-1 rounded-lg bg-rose-500 text-white text-xs font-bold">
                  匹配度 {result.demo_pair.match_percent}%
                </span>
                <p className="text-sm font-bold text-rose-600 mt-2">
                  {result.demo_pair.match_label}
                </p>
              </div>
              <div className="flex items-center justify-center gap-4">
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-rose-200 flex items-center justify-center text-3xl mx-auto relative">
                    {result.demo_pair.left.emoji}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{result.demo_pair.left.name}</p>
                </div>
                <span className="text-rose-400 text-lg">❤</span>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-full bg-white border-2 border-amber-200 flex items-center justify-center text-3xl mx-auto">
                    {result.demo_pair.right.emoji}
                  </div>
                  <p className="text-xs text-gray-600 mt-1">{result.demo_pair.right.name}</p>
                </div>
              </div>
              <div className="space-y-2 text-xs text-gray-600">
                <p>{result.demo_pair.left.line}</p>
                <p className="text-right">{result.demo_pair.right.line}</p>
              </div>
            </div>
          )}

          {fullOpen && (
            <div className="mx-1 bg-white rounded-2xl border border-gray-100 p-4 space-y-3">
              <h3 className="font-semibold text-gray-800 text-sm">完整得分分布</h3>
              {(result.breakdown || []).map((item) => (
                <div key={item.key} className="space-y-1">
                  <div className="flex justify-between text-xs">
                    <span>
                      {item.emoji} {item.name}
                    </span>
                    <span className="text-gray-500">
                      {item.score} · {item.percent}%
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${item.percent}%`, background: item.color }}
                    />
                  </div>
                </div>
              ))}
              <p className="text-[11px] text-gray-400">{result.disclaimer}</p>
            </div>
          )}

          <div className="sticky bottom-0 flex gap-2 px-1 pb-2 pt-1 bg-gradient-to-t from-rose-50/95 to-transparent">
            <button
              type="button"
              onClick={() => setFullOpen((v) => !v)}
              className="flex-1 py-3 rounded-xl border border-gray-300 bg-white text-sm font-medium text-gray-700"
            >
              {fullOpen ? "收起完整报告" : "查看完整报告"}
            </button>
            <button
              type="button"
              onClick={shareResult}
              className="flex-1 py-3 rounded-xl bg-blue-500 text-white text-sm font-medium"
            >
              分享测评
            </button>
          </div>

          <button
            type="button"
            onClick={startQuiz}
            className="w-full py-2.5 text-sm text-rose-600"
          >
            再测一次
          </button>
        </section>
      )}
    </div>
  );
}
